import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Slider,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  AutoAwesome as MagicIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  CompareArrows as CompareIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as AIIcon,
  QuestionAnswer as QuestionIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { backendApiService } from '../../services/backendApiService';
import { ContentEditor } from './ContentEditor';
import { ScorePanel } from './ScorePanel';
import { DensitySliders } from './DensitySliders';
import { CompetitorComparison } from './CompetitorComparison';
import { QuestionCoverage } from './QuestionCoverage';
import { OptimizationHistory } from './OptimizationHistory';
import { DiffViewer } from './DiffViewer';

interface ContentOptimizerProps {
  initialKeyword?: string;
  initialUrl?: string;
}

interface ContentAnalysis {
  cqcScore: number;
  keywordDensity: number;
  entityDensity: number;
  readabilityScore: number;
  wordCount: number;
  questionsTotal: number;
  questionsCovered: number;
  coveredQuestions: string[];
  missingQuestions: string[];
  entities: string[];
  aiOverviewProbability: number;
}

interface OptimizationSettings {
  targetKeywordDensity: number;
  targetEntityDensity: number;
  targetCQCScore: number;
  targetReadability: number;
  autoAddQuestions: boolean;
  naturalLanguage: boolean;
  matchCompetitor: boolean;
}

export const ContentOptimizer: React.FC<ContentOptimizerProps> = ({
  initialKeyword = '',
  initialUrl = ''
}) => {
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [url, setUrl] = useState(initialUrl);
  const [country, setCountry] = useState('UK');
  
  const [originalContent, setOriginalContent] = useState('');
  const [optimizedContent, setOptimizedContent] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  
  const [originalAnalysis, setOriginalAnalysis] = useState<ContentAnalysis | null>(null);
  const [optimizedAnalysis, setOptimizedAnalysis] = useState<ContentAnalysis | null>(null);
  
  const [settings, setSettings] = useState<OptimizationSettings>({
    targetKeywordDensity: 2.5,
    targetEntityDensity: 4.0,
    targetCQCScore: 80,
    targetReadability: 60,
    autoAddQuestions: true,
    naturalLanguage: true,
    matchCompetitor: false
  });

  const [competitors, setCompetitors] = useState<any[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch content from URL or WordPress
  const fetchContent = async () => {
    if (!url) {
      setError('Please enter a URL to fetch content');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    
    try {
      console.log('[ContentOptimizer] Fetching content from:', url);
      const response = await backendApiService.post('/api/content-optimizer/fetch', {
        url,
        method: url.includes('/wp-json/') ? 'wordpress' : 'scrape'
      });

      console.log('[ContentOptimizer] Fetch response:', response);

      if (response.success && response.data) {
        console.log('[ContentOptimizer] Content received:', {
          contentLength: response.data.content?.length,
          wordCount: response.data.wordCount,
          title: response.data.title
        });
        
        setOriginalContent(response.data.content);
        if (response.data.title && !keyword) {
          // Try to extract keyword from title
          const cleanTitle = response.data.title.replace(/\s*-\s*.*$/, '').trim();
          setKeyword(cleanTitle.toLowerCase());
        }
        setSuccessMessage('Content fetched successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to fetch content from URL');
      }
    } catch (err: any) {
      console.error('[ContentOptimizer] Fetch error:', err);
      setError(err.message || 'Error fetching content');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Analyze content
  const analyzeContent = async (content: string, isOriginal: boolean = true) => {
    if (!content || !keyword) {
      setError('Please provide content and keyword for analysis');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      console.log('[ContentOptimizer] Analyzing content:', {
        keyword,
        country,
        contentLength: content.length,
        isOriginal
      });
      
      const response = await backendApiService.post('/api/content-optimizer/analyze', {
        content,
        keyword,
        country,
        url
      });

      console.log('[ContentOptimizer] Analysis response:', response);

      if (response.success && response.data) {
        const analysis = response.data;
        
        if (isOriginal) {
          setOriginalAnalysis(analysis);
          setSessionId(response.data.sessionId);
        } else {
          setOptimizedAnalysis(analysis);
        }
        
        setSuccessMessage('Content analyzed successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Fetch competitors if we have keyword
        if (isOriginal && keyword) {
          fetchCompetitors();
        }
      }
    } catch (err: any) {
      console.error('[ContentOptimizer] Analysis error:', err);
      setError(err.message || 'Error analyzing content');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Fetch competitor data
  const fetchCompetitors = async () => {
    try {
      const response = await backendApiService.post('/api/content-optimizer/competitors', {
        keyword,
        country
      });

      if (response.success && response.data) {
        setCompetitors(response.data.competitors);
      }
    } catch (err) {
      console.error('Error fetching competitors:', err);
    }
  };

  // Apply Unicorn Magic optimization
  const applyUnicornMagic = async () => {
    if (!originalContent || !originalAnalysis) {
      setError('Please analyze content first');
      return;
    }

    setIsOptimizing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await backendApiService.post('/api/content-optimizer/optimize', {
        content: originalContent,
        keyword,
        country,
        sessionId,
        settings,
        analysis: originalAnalysis,
        competitors
      });

      if (response.success && response.data) {
        setOptimizedContent(response.data.optimizedContent);
        setOptimizedAnalysis(response.data.analysis);
        setSuccessMessage('Content optimized with Unicorn Magic! ✨');
        
        // Animate the transformation
        animateContentTransformation();
      }
    } catch (err: any) {
      setError(err.message || 'Error optimizing content');
    } finally {
      setIsOptimizing(false);
    }
  };

  // Animate content transformation
  const animateContentTransformation = () => {
    // This will trigger visual effects in the ContentEditor component
    const element = document.getElementById('optimized-content-editor');
    if (element) {
      element.classList.add('unicorn-magic-animation');
      setTimeout(() => {
        element.classList.remove('unicorn-magic-animation');
      }, 2000);
    }
  };

  // Copy optimized content
  const copyOptimizedContent = () => {
    navigator.clipboard.writeText(optimizedContent);
    setSuccessMessage('Content copied to clipboard!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Export content
  const exportContent = () => {
    const blob = new Blob([optimizedContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `optimized-content-${keyword.replace(/\s+/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccessMessage('Content exported!');
  };

  // Calculate improvement percentage
  const calculateImprovement = (original: number, optimized: number) => {
    if (!original || !optimized) return 0;
    return Math.round(((optimized - original) / original) * 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          🦄 Content Optimizer - Unicorn Magic Edition
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Target Keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g., New York dinner cruise"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              fullWidth
              label="Content URL (optional)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/page or WordPress REST API URL"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <TextField
              fullWidth
              select
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="UK">UK</option>
              <option value="USA">USA</option>
              <option value="FR">France</option>
              <option value="DE">Germany</option>
              <option value="NL">Netherlands</option>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={fetchContent}
              disabled={isAnalyzing || !url}
              sx={{ height: '56px' }}
            >
              Fetch Content
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
      </Paper>

      {/* Main Content Area */}
      <Grid container spacing={3}>
        {/* Original Content */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Original Content</Typography>
              {originalAnalysis && (
                <Chip
                  label={`CQC Score: ${originalAnalysis.cqcScore}%`}
                  color={originalAnalysis.cqcScore >= 70 ? 'success' : 'warning'}
                />
              )}
            </Box>
            
            <ContentEditor
              content={originalContent}
              onChange={setOriginalContent}
              placeholder="Paste your content here or fetch from URL..."
              readOnly={false}
              highlights={originalAnalysis?.coveredQuestions || []}
            />
            
            {originalAnalysis && (
              <ScorePanel
                analysis={originalAnalysis}
                isOriginal={true}
              />
            )}
            
            <Box sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => analyzeContent(originalContent, true)}
                disabled={isAnalyzing || !originalContent || !keyword}
                startIcon={isAnalyzing ? <CircularProgress size={20} /> : <SpeedIcon />}
              >
                {isAnalyzing ? 'Analyzing...' : 'Analyze Content'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Optimized Content */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Optimized Content</Typography>
              {optimizedAnalysis && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={`CQC: ${optimizedAnalysis.cqcScore}%`}
                    color="success"
                    icon={<TrendingUpIcon />}
                  />
                  <Chip
                    label={`+${calculateImprovement(
                      originalAnalysis?.cqcScore || 0,
                      optimizedAnalysis.cqcScore
                    )}%`}
                    color="primary"
                  />
                </Box>
              )}
            </Box>
            
            <ContentEditor
              id="optimized-content-editor"
              content={optimizedContent}
              onChange={setOptimizedContent}
              placeholder="Optimized content will appear here..."
              readOnly={false}
              highlights={optimizedAnalysis?.coveredQuestions || []}
              showDiff={true}
              originalContent={originalContent}
            />
            
            {optimizedAnalysis && (
              <ScorePanel
                analysis={optimizedAnalysis}
                isOriginal={false}
                originalAnalysis={originalAnalysis}
              />
            )}
            
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={applyUnicornMagic}
                disabled={isOptimizing || !originalAnalysis}
                startIcon={isOptimizing ? <CircularProgress size={20} /> : <MagicIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)',
                  }
                }}
              >
                {isOptimizing ? 'Applying Magic...' : 'Unicorn Magic ✨'}
              </Button>
              
              <Tooltip title="Copy">
                <IconButton onClick={copyOptimizedContent} disabled={!optimizedContent}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Export">
                <IconButton onClick={exportContent} disabled={!optimizedContent}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Control Panel */}
      <Paper sx={{ mt: 3, p: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Density Controls" />
          <Tab label="Question Coverage" />
          <Tab label="Competitor Analysis" />
          <Tab label="Optimization History" />
          {optimizedContent && <Tab label="View Changes" icon={<CompareIcon />} />}
        </Tabs>
        
        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && (
            <DensitySliders
              settings={settings}
              onChange={setSettings}
              currentAnalysis={originalAnalysis}
              onApply={(newSettings) => {
                setSettings(newSettings);
                applyUnicornMagic();
              }}
            />
          )}
          
          {activeTab === 1 && originalAnalysis && (
            <QuestionCoverage
              covered={originalAnalysis.coveredQuestions}
              missing={originalAnalysis.missingQuestions}
              onAddQuestion={(question) => {
                // Add question to content
                const newContent = optimizedContent + '\n\n' + question;
                setOptimizedContent(newContent);
                analyzeContent(newContent, false);
              }}
            />
          )}
          
          {activeTab === 2 && (
            <CompetitorComparison
              competitors={competitors}
              yourAnalysis={originalAnalysis}
              onMatchCompetitor={(competitorIndex) => {
                const competitor = competitors[competitorIndex] as any;
                if (competitor) {
                  // Set all optimization parameters to match the competitor
                  const newSettings = {
                    ...settings,
                    targetKeywordDensity: Math.round(competitor.keywordDensity * 10) / 10, // Round to 1 decimal
                    targetEntityDensity: Math.round(competitor.entityDensity * 10) / 10,
                    targetCQCScore: Math.round(competitor.cqcScore),
                    targetReadability: Math.round(competitor.readabilityScore),
                    autoAddQuestions: true, // Enable to help reach CQC target
                    naturalLanguage: true,  // Keep natural tone
                    matchCompetitor: true   // Flag that we're matching
                  };

                  setSettings(newSettings);
                  setSuccessMessage(`Optimization settings updated to match Position #${competitor.position} competitor! Click "Unicorn Magic" to apply.`);

                  // Don't auto-apply - let user review the settings first
                  // applyUnicornMagic();
                }
              }}
            />
          )}
          
          {activeTab === 3 && sessionId && (
            <OptimizationHistory
              sessionId={sessionId}
              onRestore={(content) => {
                setOptimizedContent(content);
                analyzeContent(content, false);
              }}
            />
          )}

          {activeTab === 4 && originalContent && optimizedContent && (
            <DiffViewer
              original={originalContent}
              optimized={optimizedContent}
            />
          )}
        </Box>
      </Paper>

      <style>{`
        .unicorn-magic-animation {
          animation: unicornSparkle 2s ease-in-out;
        }
        
        @keyframes unicornSparkle {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.02); opacity: 0.8; box-shadow: 0 0 20px rgba(138, 43, 226, 0.5); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </Box>
  );
};